import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import {
  Grid,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Box,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// components
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { FormProvider, RHFTextField } from '../../components/hook-form';

// ----------------------------------------------------------------------

export default function GenerateBlogTitle() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { pathname } = useLocation();

  const [results, setResults] = useState([]);

  useEffect(() => {
    const json = localStorage.getItem(pathname);
    const savedResults = JSON.parse(json);
    if (savedResults) {
      setResults(savedResults);
    }
  }, [pathname]);

  useEffect(() => {
    const json = JSON.stringify(results);
    localStorage.setItem(pathname, json);
  }, [pathname, results]);

  const { getAccessTokenSilently } = useAuth0();

  const formSchema = Yup.object().shape({
    keywords: Yup.string().required('Keywords field is required'),
  });

  const defaultValues = {
    keywords: '',
  };

  const methods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    const accessToken = await getAccessTokenSilently({
      audience: 'vrd-aigen/api',
    });

    const { keywords } = data;

    const opts = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const body = {
      keywords,
    };

    await axios
      .post(`${baseUrl}/generate/blog-title`, body, opts)
      .then((resp) => {
        const { data: response } = resp;
        setResults((prev) => [{ keywords, content: response.content, favorite: false }, ...prev]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const setFavorite = (idx, favorite) => {
    setResults((prev) => prev.map((value, i) => (i === idx ? { ...value, favorite } : value)));
  };

  const deleteResult = (idx) => {
    setResults((prev) => prev.filter((_, i) => i !== idx));
  };

  const setKeywords = (keywords) => {
    setValue('keywords', keywords);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Page title="Generate Blog Title">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Generate Blog Title
          </Typography>
        </Stack>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <RHFTextField fullWidth name="keywords" label="Keywords" inputRef={formSchema} />
          </Stack>
          <br />
          <LoadingButton
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<Iconify icon="eva:play-circle-outline" />}
          >
            Generate
          </LoadingButton>
          <br />
        </FormProvider>
        <br />
        <Grid container spacing={2}>
          {results.map((result, index) => (
            <Grid key={`${index}-${result.content}`} item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body">{result.content}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.keywords}
                  </Typography>
                </CardContent>
                <CardActions sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1 }}>
                    <Tooltip title="Favorite">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setFavorite(index, !result.favorite);
                        }}
                      >
                        <Iconify icon={`eva:heart-${result.favorite ? 'fill' : 'outline'}`} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => setKeywords(result.keywords)}>
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate Blog Outline">
                      <Button
                        color="success"
                        component={RouterLink}
                        state={{ title: result.content, keywords: result.keywords }}
                        to="/dashboard/blog/outline"
                      >
                        <Iconify icon="eva:arrow-right-fill" /> outline
                      </Button>
                    </Tooltip>
                    <Tooltip title="Generate Blog Introduction">
                      <Button
                        color="success"
                        component={RouterLink}
                        state={{ title: result.content, keywords: result.keywords }}
                        to="/dashboard/blog/intro"
                      >
                        <Iconify icon="eva:arrow-right-fill" /> introduction
                      </Button>
                    </Tooltip>
                  </Box>
                  <IconButton color="error" onClick={() => deleteResult(index)}>
                    <Iconify icon="eva:trash-2-fill" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Page>
  );
}
